// ─── src/api/npd.ts ──────────────────────────────────────────────────────────
//
// Admin API for the National Pitching Day inboxes: investor RSVPs (live for
// the current cycle) and founder waitlist nominations (interest capture for
// the next cycle). Both endpoints are x-api-key guarded on the backend.

import { apiClient } from "./registration";
import type { PaginationMeta } from "../types/registration";

// ── RSVP side ────────────────────────────────────────────────────────────────

export interface NpdInvestorRsvp {
  id: string;
  fullName: string;
  role: string;
  firmName: string;
  email: string;
  phone: string;
  plusOnes?: string | null;
  dietaryNotes?: string | null;
  declarationContact: string;
  createdAt: string;
  updatedAt: string;
}

export interface NpdRsvpParams {
  page?: number;
  limit?: number;
  search?: string;
  firmName?: string;
}

export interface NpdRsvpStats {
  total: number;
  byFirm: Record<string, number>;
}

// ── Waitlist nomination side ─────────────────────────────────────────────────

export interface NpdNomination {
  id: string;
  founderName: string;
  founderEmail: string;
  startupName: string;
  governorate: string;
  oneLiner?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NpdNominationParams {
  page?: number;
  limit?: number;
  search?: string;
  governorate?: string;
}

export interface NpdNominationStats {
  total: number;
  byGovernorate: Record<string, number>;
}

// ── Full registration side ───────────────────────────────────────────────────

export interface NpdRegistration {
  id: string;
  fullName: string;
  email: string;
  whatsapp: string;
  gender: string;
  ageBracket: string;
  governorate: string;
  city: string;
  currentStatus: string;
  skills: string[];
  linkedinUrl?: string | null;
  startupName: string;
  startupDescription: string;
  startupCity: string;
  sector: string;
  establishYear?: number | null;
  stage: string;
  problemSolved: string;
  founders: Array<{ name: string; linkedinUrl: string | null }>;
  /** Legacy columns from earlier form versions — null for new submissions. */
  differentiator?: string | null;
  targetAudience?: string | null;
  motivation?: string | null;
  hasUsersOrRevenue: boolean;
  annualRevenueRange?: string | null;
  customerCount?: number | null;
  adminStatus: NpdAdminStatus;
  adminNotes?: string | null;
  adminTags: string[];
  createdAt: string;
  updatedAt: string;
}

export type NpdAdminStatus = "new_applicant" | "shortlisted" | "final";

export interface NpdRegistrationReviewPatch {
  adminStatus?: NpdAdminStatus;
  adminNotes?: string | null;
  adminTags?: string[];
}

export interface NpdRegistrationParams {
  page?: number;
  limit?: number;
  search?: string;
  governorate?: string;
  sector?: string;
  stage?: string;
  adminStatus?: NpdAdminStatus;
}

export interface NpdRegistrationStats {
  total: number;
  byGovernorate: Record<string, number>;
  bySector: Record<string, number>;
  byStage: Record<string, number>;
  byAdminStatus: Record<string, number>;
}

// ── Shared list shape ────────────────────────────────────────────────────────

interface Paged<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface NormalisedList<T> {
  data: T[];
  meta: PaginationMeta;
}

const normalise = <T,>(raw: Paged<T>): NormalisedList<T> => ({
  data: raw.data,
  meta: {
    total: raw.total,
    page: raw.page,
    limit: raw.limit,
    totalPages: Math.ceil(raw.total / raw.limit),
  },
});

const clean = <T extends object>(params: T) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== "" && v !== null,
    ),
  );

// ── Calls ────────────────────────────────────────────────────────────────────

export const getNpdRsvps = async (
  params: NpdRsvpParams,
): Promise<NormalisedList<NpdInvestorRsvp>> => {
  const { data } = await apiClient.get<Paged<NpdInvestorRsvp>>("/npd/rsvps", {
    params: clean(params),
  });
  return normalise(data);
};

export const getNpdRsvpStats = async (): Promise<NpdRsvpStats> => {
  const { data } = await apiClient.get<NpdRsvpStats>("/npd/rsvps/stats");
  return data;
};

export const deleteNpdRsvp = async (id: string): Promise<void> => {
  await apiClient.delete(`/npd/rsvps/${id}`);
};

export const getNpdNominations = async (
  params: NpdNominationParams,
): Promise<NormalisedList<NpdNomination>> => {
  const { data } = await apiClient.get<Paged<NpdNomination>>(
    "/npd/nominations",
    { params: clean(params) },
  );
  return normalise(data);
};

export const getNpdNominationStats = async (): Promise<NpdNominationStats> => {
  const { data } = await apiClient.get<NpdNominationStats>(
    "/npd/nominations/stats",
  );
  return data;
};

export const deleteNpdNomination = async (id: string): Promise<void> => {
  await apiClient.delete(`/npd/nominations/${id}`);
};

export const getNpdRegistrations = async (
  params: NpdRegistrationParams,
): Promise<NormalisedList<NpdRegistration>> => {
  const { data } = await apiClient.get<Paged<NpdRegistration>>(
    "/npd/registrations",
    { params: clean(params) },
  );
  return normalise(data);
};

export const getNpdRegistrationStats =
  async (): Promise<NpdRegistrationStats> => {
    const { data } = await apiClient.get<NpdRegistrationStats>(
      "/npd/registrations/stats",
    );
    return data;
  };

export const deleteNpdRegistration = async (id: string): Promise<void> => {
  await apiClient.delete(`/npd/registrations/${id}`);
};

export const reviewNpdRegistration = async (
  id: string,
  patch: NpdRegistrationReviewPatch,
): Promise<NpdRegistration> => {
  const { data } = await apiClient.patch<NpdRegistration>(
    `/npd/registrations/${id}`,
    patch,
  );
  return data;
};
